import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import UserCard from './cardPlayer';

interface TUser {
  userId: number;
  nom: string;
  prenom: string;
  civiliteId: number;
  departementId: number;
  niveauId: number;
}

interface PublicPartnerPreviewProps {
  civilites: any[];
  deps: any[];
  niveau: any[];
}

const PublicPartnerPreview: React.FC<PublicPartnerPreviewProps> = ({ 
  civilites, 
  deps, 
  niveau 
}) => {
  const [users, setUsers] = useState<TUser[]>([]);
  const [totalPartners, setTotalPartners] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        // Récupérer les utilisateurs preview et le comptage total en parallèle
        const [usersResponse, countResponse] = await Promise.all([
          api.get('/users/public/preview'),
          api.get('/users/public/count')
        ]);
        
        setUsers(usersResponse.data);
        setTotalPartners(countResponse.data.total);
      } catch (error) {
        console.error('Error fetching public users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicData();
  }, []);

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="w-full">
      {/* Bannière d'encouragement à l'inscription - Modernisée */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl p-8 md:p-10 mb-12 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold mb-4 flex items-center gap-2">
              <span className="text-3xl">🎾</span>
              Aperçu des partenaires
            </h3>
            <p className="text-lg text-orange-50 mb-6 leading-relaxed">
              Explorez notre sélection de {users.length} partenaires. Inscrivez-vous gratuitement pour accéder à <strong>tous les profils</strong>, leurs <strong>disponibilités</strong> et les <strong>fonctionnalités de messagerie</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a 
                href="/signup" 
                className="bg-white text-orange-600 px-7 py-3 rounded-xl font-bold hover:bg-orange-50 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 text-center inline-block"
              >
                S'inscrire gratuitement
              </a>
              <a 
                href="/login" 
                className="border-2 border-white text-white px-7 py-3 rounded-xl font-bold hover:bg-white hover:text-orange-600 transition-all duration-300 text-center inline-block"
              >
                Se connecter
              </a>
            </div>
          </div>
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-bold text-white mb-2">{totalPartners}+</div>
            <p className="text-2xl text-orange-100 font-semibold">Partenaires vous attendent</p>
          </div>
        </div>
      </div>

      {/* Titre section */}
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Découvrez nos partenaires actifs
        </h2>
        <p className="text-gray-600">Voici un aperçu des joueurs disponibles sur notre plateforme</p>
      </div>

      {/* Grille des utilisateurs publics - Améliorée */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
        {users.map((user) => (
          <div key={user.userId} className="relative group">
            <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <UserCard
                userId={user.userId}
                prenom={user.prenom}
                nom=""
                genre={civilites.find(c => c.civiliteId === user.civiliteId)?.civiliteLib || ''}
                main="Connectez-vous"
                departement={deps.find(h => h.departementId === user.departementId)?.departementNum + ' - ' + deps.find(h => h.departementId === user.departementId)?.departementLib || ''}
                niveau={niveau.find(h => h.niveauId === user.niveauId)?.niveauOrdre + ' - ' + niveau.find(h => h.niveauId === user.niveauId)?.niveauLib || ''}
                classement={0}
                dispoJeu="Connectez-vous"
                dispoTournoi="Connectez-vous"
                onMessageClick={() => {
                  alert('Connectez-vous pour envoyer des messages !');
                }}
              />
              
              {/* Overlay moderne */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-end justify-center pb-4">
                <div className="text-white text-center">
                  <p className="text-lg font-bold mb-1">Connectez-vous</p>
                  <p className="text-sm text-gray-200">pour voir plus de détails</p>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 mx-auto mt-2 animate-bounce">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Call-to-action final - Modernisé */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-2xl p-10 md:p-12 text-center">
        <div className="mb-6">
          <div className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 mb-4">
            {totalPartners > 0 ? `${totalPartners}+` : "Rejoignez"}
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            {totalPartners > 0 ? `${totalPartners} partenaire${totalPartners > 1 ? 's' : ''} disponible${totalPartners > 1 ? 's' : ''}` : 'Notre communauté grandissante'}
          </h3>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Accédez à un réseau complet de joueurs de padel, des filtres avancés de recherche et des fonctionnalités de messagerie pour organiser vos matchs.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a 
            href="/signup" 
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-4 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2"
          >
            Rejoindre maintenant
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
          <a 
            href="/login" 
            className="border-2 border-orange-500 text-orange-600 px-10 py-4 rounded-xl font-bold hover:bg-orange-50 transition-all duration-300 inline-flex items-center gap-2"
          >
            Déjà inscrit ?
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
            </svg>
          </a>
        </div>

        {/* Points de confiance */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-700 pt-8 border-t border-orange-200">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Inscription gratuite</span>
          </div>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Accès immédiat</span>
          </div>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Sans engagement</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPartnerPreview;